import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, Plus, Check, X, GripVertical } from 'lucide-react';
import useDragReorder from '../../hooks/useDragReorder';
import { sans, mono, green, greenLight, red, redLight, amber, amberLight, useTheme } from '../../tokens';
import { useCurrency } from '../../tokens';
import { fmtCompactC } from '../../helpers';
import { getRisks, saveRisks, subscribeToRisksChanges, newRiskId } from '../../data/risksStore';

// INV-15 — Top 5 risks with status indicators and severity badges

// Single severity enum used by both data and UI. Any value outside of
// { high, medium, low } will be normalized below so filter counts and
// visuals always agree.
const SEVERITY_ALIASES = {
  red: 'high', critical: 'high', high: 'high',
  yellow: 'medium', amber: 'medium', medium: 'medium',
  green: 'low', low: 'low',
};

function normalizeSeverity(s) {
  return SEVERITY_ALIASES[(s || '').toLowerCase()] || 'medium';
}

const SEVERITY_COLORS = {
  high: red,
  medium: amber,
  low: green,
};

const SEVERITY_BG = {
  high: redLight,
  medium: amberLight,
  low: greenLight,
};

const RISKS_FALLBACK = [
  {
    id: 1, severity: 'high',
    title: 'Clinical trial enrollment delays',
    description: 'EFS Georgia enrollment pace may slow due to site readiness and patient screening criteria.',
    mitigation: 'Activated backup clinical site in Atlanta. Expanded screening criteria per FDA feedback. Weekly enrollment tracking calls with PI.',
    owner: 'VP Clinical',
    lastUpdated: '2026-03-15',
  },
  {
    id: 2, severity: 'high',
    title: 'SPCC ownership threshold erosion',
    description: 'Canadian ownership at 58.7%, approaching the 55% warning threshold for SPCC status.',
    mitigation: 'Board-approved Canadian investor allocation policy for Series B. Tracking ownership quarterly. SR&ED impact analysis completed.',
    owner: 'CFO',
    lastUpdated: '2026-02-28',
  },
  {
    id: 3, severity: 'medium',
    title: 'Supply chain for critical components',
    description: 'Single-source dependency for micro-catheter tubing from EU supplier.',
    mitigation: 'Qualification of secondary supplier in progress (expected Q2 2026). 6-month safety stock maintained.',
    owner: 'VP Operations',
    lastUpdated: '2026-03-01',
  },
  {
    id: 4, severity: 'medium',
    title: 'Regulatory timeline uncertainty',
    description: 'FDA/HC review timelines for EFS expansion may extend beyond Q3 2026.',
    mitigation: 'Pre-submission meetings completed with favorable feedback. Engaged regulatory consultants for parallel filing strategy.',
    owner: 'VP Regulatory',
    lastUpdated: '2026-02-20',
  },
  {
    id: 5, severity: 'low',
    title: 'Key person risk -- engineering leadership',
    description: 'Concentration of institutional knowledge in senior engineering team.',
    mitigation: 'Documentation initiative underway. Cross-training program launched Q1 2026. Retention packages renewed.',
    owner: 'VP HR',
    lastUpdated: '2026-01-15',
  },
];

function formatDate(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    if (!isNaN(d.getTime())) return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  }
  return dateStr;
}

export default function RisksCard({ companyId, readOnly = false }) {
  const t = useTheme();
  const [expandedRisk, setExpandedRisk] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ title: '', severity: 'medium', description: '', mitigation: '', owner: '' });

  const [rawRisks, setRawRisks] = useState(() => {
    const stored = getRisks(companyId);
    return stored.length ? stored : RISKS_FALLBACK.map(r => ({ ...r }));
  });
  useEffect(() => subscribeToRisksChanges(() => setRawRisks(getRisks(companyId))), [companyId]);

  const risks = rawRisks.map(r => ({ ...r, severity: normalizeSeverity(r.severity) }));

  const persist = (next) => {
    setRawRisks(next);
    saveRisks(companyId, next);
  };
  const startEdit = (r) => {
    setEditingId(r.id);
    setDraft({
      title: r.title || '',
      severity: normalizeSeverity(r.severity),
      description: r.description || '',
      mitigation: r.mitigation || '',
      owner: r.owner || '',
    });
  };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = () => {
    if (!draft.title.trim()) return;
    const nowDate = new Date().toISOString().split('T')[0];
    persist(rawRisks.map(r => r.id === editingId ? { ...r, ...draft, lastUpdated: nowDate } : r));
    setEditingId(null);
  };
  const removeRisk = (id) => {
    persist(rawRisks.filter(r => r.id !== id));
  };
  const drag = useDragReorder(rawRisks, persist);
  const addRisk = () => {
    const id = newRiskId();
    const nowDate = new Date().toISOString().split('T')[0];
    const r = { id, title: 'New risk', severity: 'medium', description: '', mitigation: '', owner: '', lastUpdated: nowDate };
    persist([r, ...rawRisks]);
    startEdit(r);
  };

  // Compact edit form — one type scale, no oversized anything.
  const FORM_LABEL_SIZE = 8;
  const FORM_INPUT_SIZE = 11;
  const inputStyle = {
    padding: '5px 8px', borderRadius: 4, border: `1px solid ${t.border}`,
    background: t.bgCard, color: t.textPrimary, fontFamily: sans,
    fontSize: FORM_INPUT_SIZE, fontWeight: 400, lineHeight: 1.4,
    outline: 'none', width: '100%', boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block', fontSize: FORM_LABEL_SIZE, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 0.4, color: t.textMuted,
    fontFamily: sans, marginBottom: 3,
  };

  return (
    <div style={{
      background: t.bgCard,
      border: `1px solid ${t.border}`,
      borderRadius: 10,
      padding: '20px 24px',
    }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{
          fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: 0.5, color: t.textMuted, fontFamily: sans,
        }}>
          Key Risks
        </div>
        <div style={{ fontSize: 10, color: t.textFaint, fontFamily: sans, marginTop: 2 }}>
          Top risk register items with mitigations
        </div>
      </div>

      {/* Severity summary bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        {[
          { label: 'High', count: risks.filter(r => r.severity === 'high').length, bg: redLight, color: red },
          { label: 'Medium', count: risks.filter(r => r.severity === 'medium').length, bg: amberLight, color: amber },
          { label: 'Low', count: risks.filter(r => r.severity === 'low').length, bg: greenLight, color: green },
        ].map(s => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 12, background: s.bg }}>
            <span style={{ fontSize: 14, fontWeight: 700, fontFamily: mono, color: s.color }}>{s.count}</span>
            <span style={{ fontSize: 9, fontWeight: 600, color: s.color, fontFamily: sans }}>{s.label}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {risks.map((risk, i) => {
          const isExpanded = expandedRisk === risk.id;
          const isEditing = editingId === risk.id;
          const sevColor = SEVERITY_COLORS[risk.severity] || t.textMuted;
          const sevBg = SEVERITY_BG[risk.severity] || t.pillBg;

          const rowDrag = !readOnly && !isEditing ? drag.rowProps(risk.id) : {};
          const isDragTarget = drag.dragOverId === risk.id;
          return (
            <div key={risk.id}
              {...rowDrag}
              style={{
                ...(rowDrag.style || {}),
                borderBottom: i < risks.length - 1 ? `1px solid ${t.borderLight}` : 'none',
                background: isDragTarget ? greenLight : 'transparent',
                transition: 'background 0.15s',
              }}>
              {isEditing ? (
                <div style={{
                  margin: '6px 0',
                  padding: '10px 12px',
                  background: t.bgSurface,
                  borderRadius: 6,
                  borderLeft: `3px solid ${SEVERITY_COLORS[draft.severity] || sevColor}`,
                }}>
                  {/* Title row: title + severity pills on same line */}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Title</label>
                      <input
                        value={draft.title}
                        onChange={e => setDraft({ ...draft, title: e.target.value })}
                        placeholder="e.g. Regulatory delay"
                        autoFocus
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Severity</label>
                      <div style={{ display: 'flex', gap: 3 }}>
                        {[
                          { key: 'high',   label: 'High',   color: red,   bg: redLight },
                          { key: 'medium', label: 'Med',    color: amber, bg: amberLight },
                          { key: 'low',    label: 'Low',    color: green, bg: greenLight },
                        ].map(s => {
                          const selected = draft.severity === s.key;
                          return (
                            <button
                              key={s.key}
                              type="button"
                              onClick={() => setDraft({ ...draft, severity: s.key })}
                              style={{
                                padding: '3px 10px', borderRadius: 12,
                                border: `1px solid ${selected ? s.color : t.border}`,
                                background: selected ? s.bg : 'transparent',
                                color: selected ? s.color : t.textMuted,
                                fontSize: 9, fontWeight: 700, fontFamily: sans,
                                cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 0.3,
                              }}
                            >
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <label style={labelStyle}>Risk</label>
                  <textarea
                    value={draft.description}
                    onChange={e => setDraft({ ...draft, description: e.target.value })}
                    placeholder="What is the risk?"
                    rows={2}
                    style={{ ...inputStyle, resize: 'vertical', marginBottom: 8 }}
                  />

                  {/* Mitigation */}
                  <label style={labelStyle}>Mitigation</label>
                  <textarea
                    value={draft.mitigation}
                    onChange={e => setDraft({ ...draft, mitigation: e.target.value })}
                    placeholder="How we're mitigating it"
                    rows={2}
                    style={{ ...inputStyle, resize: 'vertical', marginBottom: 8 }}
                  />

                  {/* Owner + actions on one row */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Owner</label>
                      <input
                        value={draft.owner}
                        onChange={e => setDraft({ ...draft, owner: e.target.value })}
                        placeholder="e.g. CFO"
                        style={inputStyle}
                      />
                    </div>
                    <button onClick={cancelEdit} style={{
                      display: 'flex', alignItems: 'center', gap: 3,
                      background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 4,
                      padding: '5px 10px', color: t.textSecondary,
                      fontFamily: sans, fontSize: FORM_INPUT_SIZE, fontWeight: 600, cursor: 'pointer',
                    }}><X size={11} /> Cancel</button>
                    <button onClick={saveEdit} disabled={!draft.title.trim()} style={{
                      display: 'flex', alignItems: 'center', gap: 3,
                      background: draft.title.trim() ? green : t.borderLight,
                      border: 'none', borderRadius: 4,
                      padding: '5px 12px', color: draft.title.trim() ? '#fff' : t.textMuted,
                      fontFamily: sans, fontSize: FORM_INPUT_SIZE, fontWeight: 600,
                      cursor: draft.title.trim() ? 'pointer' : 'not-allowed',
                    }}><Check size={11} /> Save</button>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    onClick={() => setExpandedRisk(isExpanded ? null : risk.id)}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '10px 0', cursor: 'pointer',
                    }}
                  >
                    <div style={{
                      width: 3, minHeight: 32, borderRadius: 2,
                      background: sevColor, flexShrink: 0, marginTop: 2,
                    }} />

                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{
                            fontSize: 8, fontWeight: 700, textTransform: 'uppercase',
                            padding: '2px 8px', borderRadius: 10,
                            background: sevBg, color: sevColor,
                            marginRight: 8,
                          }}>{risk.severity.toUpperCase()}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, fontFamily: sans, color: t.textPrimary }}>
                            {risk.title}
                          </span>
                        </span>
                        <span style={{ fontSize: 8, fontFamily: mono, color: t.textFaint, flexShrink: 0, marginLeft: 8 }}>
                          {formatDate(risk.lastUpdated)}
                        </span>
                      </div>
                      <div style={{ fontSize: 10, fontFamily: sans, color: t.textMuted, marginTop: 2, lineHeight: 1.4 }}>
                        {risk.description}
                      </div>
                    </div>

                    {!readOnly && (
                      <div style={{ display: 'flex', gap: 2, flexShrink: 0, marginTop: 2 }} onClick={e => e.stopPropagation()}>
                        <span title="Drag to reorder" style={{ display: 'flex', alignItems: 'center', color: t.textFaint, padding: 2, cursor: 'grab' }}>
                          <GripVertical size={12} />
                        </span>
                        <button onClick={() => startEdit(risk)} title="Edit" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 4 }}>
                          <Pencil size={12} />
                        </button>
                        <button onClick={() => removeRisk(risk.id)} title="Delete" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: red, padding: 4 }}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}

                    <span style={{
                      fontSize: 10, color: t.textFaint, flexShrink: 0, marginTop: 4,
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s',
                    }}>&#9654;</span>
                  </div>

                  {isExpanded && (
                    <div style={{
                      padding: '0 0 12px 13px',
                      borderLeft: `2px solid ${t.borderLight}`,
                      marginBottom: 4,
                    }}>
                      <div style={{ background: t.bgSurface, borderRadius: 6, padding: '10px 14px' }}>
                        <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.4, color: t.textMuted, fontFamily: sans, fontWeight: 600, marginBottom: 4 }}>
                          Mitigation
                        </div>
                        <div style={{ fontSize: 11, fontFamily: sans, color: t.textSecondary, lineHeight: 1.5 }}>
                          {risk.mitigation || <span style={{ color: t.textFaint, fontStyle: 'italic' }}>No mitigation recorded yet.</span>}
                        </div>
                        <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                          {risk.owner && (
                            <span style={{ fontSize: 9, fontFamily: sans, color: t.textFaint }}>
                              Owner: <span style={{ color: t.textMuted, fontWeight: 600 }}>{risk.owner}</span>
                            </span>
                          )}
                          <span style={{ fontSize: 9, fontFamily: sans, color: t.textFaint }}>
                            Severity: <span style={{ color: sevColor, fontWeight: 600, textTransform: 'capitalize' }}>{risk.severity}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {!readOnly && (
        <button onClick={addRisk} style={{
          marginTop: 10, padding: '6px 12px', borderRadius: 6,
          border: `1px dashed ${t.border}`, background: 'transparent',
          color: t.textMuted, fontFamily: sans, fontSize: 11, fontWeight: 600,
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Plus size={12} /> Add risk
        </button>
      )}
    </div>
  );
}
